import React from 'react';
import { ChevronRight, MessageSquare } from 'lucide-react';
import skylarqLogo from '../../assets/skylarq_logo_small.png';
import { AgenticChatPanel } from '../chat/AgenticChatPanel';
import { IdealCustomerProfile, LeadGenProgress } from '../../types';
import type { Monitor } from '../../types/monitoring';
import type { CampaignCompletionSummary } from '../chat/campaign/types';
import { TableColumn } from '../../hooks/dashboard';
import { CHAT_PANEL } from '../../constants/layout';
import { transition } from '../../styles/tokens';

export interface ChatTableContext {
  name: string;
  type: 'companies' | 'people' | 'custom';
  columns: TableColumn[];
  status: string;
  description?: string;
  criteria?: string[];
  monitors: Monitor[];
  data: {
    provider_metadata: {
      total_analyzed: number;
      total_found: number;
    };
  };
}

export interface CampaignContext {
  totalLeadCount: number;
  filteredLeadCount: number | undefined;
  selectedLeadCount: number | undefined;
  selectedLeadIds: string[] | undefined;
  hasActiveFilters: boolean;
  rows?: Array<{ id: string; data: Record<string, any>; [key: string]: any }>;
}

export interface EnrichmentContext {
  rowCount: number;
  columns: string[];
  preSelectedColumns?: string[];
  triggeredFromChat: boolean;
}

export interface FindMoreContext {
  currentCount: number;
  initialCount?: number;
  triggeredFromChat: boolean;
}

export interface ChatPanelContainerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  tableId: string;
  tableContext: ChatTableContext;
  icpContext: IdealCustomerProfile | null;
  leadCount: number;
  progressData?: LeadGenProgress;
  initialMessages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    criteria?: string[];
  }>;
  // Actions
  onActionTriggered: (action: { type: string; payload?: any }) => void;
  onExportCSV: () => void;
  onExportExcel: () => void;
  onShare: () => void;
  onDelete: () => void;
  onCreateWorkflow: () => void;
  // Monitor setup
  showMonitorSetup: boolean;
  onMonitorSetupComplete: (keepMessages?: boolean) => void;
  onMonitorSetupCancel: () => void;
  // Campaign creation
  showCampaignCreation: boolean;
  campaignContext: CampaignContext;
  onCampaignCreated: (campaignId: string, summary?: CampaignCompletionSummary) => void;
  onCampaignCancel: () => void;
  onCampaignEnrichData: (missingColumns: string[]) => void;
  // Row selection
  onEnterRowSelectionMode: () => void;
  onExitRowSelectionMode: () => void;
  onSelectAllRows: () => void;
  onSelectFirstNRows: (n: number) => void;
  // Enrichment
  showEnrichmentFlow: boolean;
  enrichmentContext: EnrichmentContext;
  onEnrichmentComplete: () => void;
  onEnrichmentCancel: () => void;
  onEnrichmentStarted: (columnName: string) => void;
  // Find more
  showFindMoreFlow: boolean;
  findMoreContext: FindMoreContext;
  onFindMoreComplete: () => void;
  onFindMoreCancel: () => void;
  onFindMoreStarted: (count: number) => void;
}

export const ChatPanelContainer: React.FC<ChatPanelContainerProps> = ({
  isOpen,
  onClose,
  onOpen,
  tableId,
  tableContext,
  icpContext,
  leadCount,
  progressData,
  initialMessages,
  onActionTriggered,
  onExportCSV,
  onExportExcel,
  onShare,
  onDelete,
  onCreateWorkflow,
  showMonitorSetup,
  onMonitorSetupComplete,
  onMonitorSetupCancel,
  showCampaignCreation,
  campaignContext,
  onCampaignCreated,
  onCampaignCancel,
  onCampaignEnrichData,
  onEnterRowSelectionMode,
  onExitRowSelectionMode,
  onSelectAllRows,
  onSelectFirstNRows,
  showEnrichmentFlow,
  enrichmentContext,
  onEnrichmentComplete,
  onEnrichmentCancel,
  onEnrichmentStarted,
  showFindMoreFlow,
  findMoreContext,
  onFindMoreComplete,
  onFindMoreCancel,
  onFindMoreStarted,
}) => {
  return (
    <>
      {/* Chat Panel */}
      <div
        data-tour="chat-panel"
        className="flex-shrink-0 border-l border-gray-200 flex flex-col bg-white overflow-hidden"
        style={{
          width: isOpen ? CHAT_PANEL.widthPx : '0px',
          opacity: isOpen ? 1 : 0,
          transition: `width ${transition.duration.panel} ${transition.timing.panel}, opacity ${transition.duration.slow} ${transition.timing.easeOut}`,
          borderLeftWidth: isOpen ? '1px' : '0px',
        }}
      >
        <div style={{ width: CHAT_PANEL.widthPx }} className="h-full flex flex-col">
          {/* Chat Header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <img src={skylarqLogo} alt="Skylarq" className="w-7 h-7 object-contain" />
              <span className="text-[14px] font-semibold text-gray-900">Skylarq</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close chat"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Chat content */}
          <div data-tour="side-panel" className="flex-1 overflow-hidden">
            <AgenticChatPanel
              key={tableId}
              tableId={tableId}
              tableContext={tableContext}
              icpContext={icpContext}
              onActionTriggered={onActionTriggered}
              onExportCSV={onExportCSV}
              onExportExcel={onExportExcel}
              onShare={onShare}
              onDelete={onDelete}
              onCreateWorkflow={onCreateWorkflow}
              leadCount={leadCount}
              defaultOpen={true}
              embedded={true}
              hideHeader={true}
              showMonitorSetup={showMonitorSetup}
              onMonitorSetupComplete={onMonitorSetupComplete}
              onMonitorSetupCancel={onMonitorSetupCancel}
              showCampaignCreation={showCampaignCreation}
              campaignContext={campaignContext}
              onCampaignCreated={onCampaignCreated}
              onCampaignCancel={onCampaignCancel}
              onCampaignEnrichData={onCampaignEnrichData}
              onEnterRowSelectionMode={onEnterRowSelectionMode}
              onExitRowSelectionMode={onExitRowSelectionMode}
              onSelectAllRows={onSelectAllRows}
              onSelectFirstNRows={onSelectFirstNRows}
              showEnrichmentFlow={showEnrichmentFlow}
              enrichmentContext={enrichmentContext}
              onEnrichmentComplete={onEnrichmentComplete}
              onEnrichmentCancel={onEnrichmentCancel}
              onEnrichmentStarted={onEnrichmentStarted}
              showFindMoreFlow={showFindMoreFlow}
              findMoreContext={findMoreContext}
              onFindMoreComplete={onFindMoreComplete}
              onFindMoreCancel={onFindMoreCancel}
              onFindMoreStarted={onFindMoreStarted}
              progressData={progressData}
              initialMessages={initialMessages}
            />
          </div>
        </div>
      </div>

      {/* Floating button to reopen chat when minimized */}
      <button
        onClick={onOpen}
        data-tour="mobile-chat-button"
        className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 bg-gray-900 text-white text-[13px] font-medium rounded-full shadow-lg hover:bg-gray-800 z-50"
        style={{
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'translateY(20px)' : 'translateY(0)',
          pointerEvents: isOpen ? 'none' : 'auto',
          transition: `opacity ${transition.duration.slow} ${transition.timing.easeOut}, transform ${transition.duration.panel} ${transition.timing.panel}`,
        }}
      >
        <MessageSquare className="w-4 h-4" />
        Chat
      </button>
    </>
  );
};
